import React, { useEffect, useRef, useState } from 'react';
import { Icon } from 'semantic-ui-react';
import { Service } from '../../types/Service';
import { Document } from '../../types/Document';
import { fetchAllServicesInCategory, fetchServiceImage } from '../../services/Service';
import { getAssetById } from '../../services/Asset';
import { resolveServiceDocuments } from '../../services/Documents';
import { getAttachedFileLink } from '../../utils/document';
import LoadMore from '../../../utils/LoadMore';
import sendToast from '../../../utils/Toast';
import { settleAll } from '../../utils/async';

interface CreateOrderStepTwoProps {
    FacilityId: string;
    selectedServiceCategoryName: string;
    selectedResidentId: string;
    selectedServiceId: string;
    setSelectedServiceId: React.Dispatch<React.SetStateAction<string>>;
    setIsFetching: React.Dispatch<React.SetStateAction<boolean>>;
    isFetching: boolean;
}

// A bare domain like "google.com" resolves relative to the app when used as an
// href, turning into an in-app sub-route instead of navigating out to the site.
const normalizeUrl = (url: string): string =>
    /^https?:\/\//i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;

interface ServiceListCardProps {
    service: Service;
    serviceImage: string;
    FacilityId: string;
    isSelected: boolean;
    onSelect: () => void;
}

const ServiceListCard: React.FC<ServiceListCardProps> = ({ service, serviceImage, FacilityId, isSelected, onSelect }) => {
    const [assetName, setAssetName] = useState<string>('');
    const [assetUrl, setAssetUrl] = useState<string>('');
    const [serviceDocs, setServiceDocs] = useState<Document[]>([]);
    const [loadingDocs, setLoadingDocs] = useState<boolean>(true);

    useEffect(() => {
        const fetchAsset = async () => {
            if (!service.Asset) {
                setAssetName('');
                setAssetUrl('');
                return;
            }
            try {
                const assetRes = await getAssetById({ Facility: FacilityId, AssetId: service.Asset });
                if (assetRes && assetRes.response) {
                    setAssetName(assetRes.response.AssetName || '');
                    setAssetUrl(assetRes.response.URL || assetRes.response.SignedUrl || '');
                }
            } catch (error) {
                setAssetName('');
                setAssetUrl('');
                sendToast('error', error instanceof Error ? error.message : 'An error occurred while fetching asset details');
            }
        };
        fetchAsset();
    }, [service.Asset, FacilityId]);

    useEffect(() => {
        const fetchDocs = async () => {
            try {
                setLoadingDocs(true);
                const docs = await resolveServiceDocuments(FacilityId, service);
                setServiceDocs(docs);
            } catch (error) {
                setServiceDocs([]);
            } finally {
                setLoadingDocs(false);
            }
        };
        fetchDocs();
    }, [service?.DocIds, FacilityId]);

    const addOns = service.ServiceAddOns || [];

    return (
        <div
            className={`service-card${isSelected ? ' selected' : ''}`}
            role="button"
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect();
                }
            }}
        >
            <div className="service-card-icon">
                {serviceImage ? (
                    <img src={serviceImage} alt={service.name} />
                ) : (
                    <span className="service-card-icon-missing">No image</span>
                )}
            </div>
            <div className="service-card-info">
                <div className="service-card-name">{service.name}</div>
                {service.shortDescription && (
                    <div className="service-card-desc">{service.shortDescription}</div>
                )}
                {assetName && (
                    <div className="service-card-meta">
                        Asset:{' '}
                        {assetUrl ? (
                            <a
                                href={normalizeUrl(assetUrl)}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {assetName}
                            </a>
                        ) : assetName}
                    </div>
                )}
                {(loadingDocs || serviceDocs.length > 0) && (
                    <div className="service-card-meta">
                        Attached Docs:{' '}
                        {loadingDocs ? <Icon name="spinner" loading /> : serviceDocs.map((doc) => {
                            const href = getAttachedFileLink(doc);
                            if (!href) return null;
                            return (
                                <a
                                    key={doc._id || doc.Name}
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ marginLeft: 6 }}
                                >
                                    {doc.Name}
                                </a>
                            );
                        })}
                    </div>
                )}
                {addOns.length > 0 && (
                    <div className="service-card-addons-wrap">
                        <div className="service-card-addons-label">Add-Ons Available</div>
                        <div className="service-card-addons">
                            {addOns.map((addon) => (
                                <span key={addon.id} className="service-card-addon-chip">
                                    <Icon name="add" />
                                    {addon.itemName}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <Icon className="service-card-check" name="check" />
        </div>
    );
};

// Service picker for the order-status "Create Order" wizard modal — same data
// source/rules as ServiceOrderWidget/StepTwo, but a service click only selects
// (highlighting the card + check mark) instead of auto-advancing, so the modal's
// shared Back/Next footer can drive navigation to match order-status-page.html.
const CreateOrderStepTwo: React.FC<CreateOrderStepTwoProps> = ({
    FacilityId, selectedServiceCategoryName, selectedResidentId, selectedServiceId, setSelectedServiceId, setIsFetching, isFetching,
}) => {
    const [services, setServices] = useState<Service[]>([]);
    const [page_no, setPage_no] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(false);
    const [serviceImages, setServiceImages] = useState<Record<string, string>>({});
    const lastQueryKeyRef = useRef<string>('');
    const page_size = 5;

    useEffect(() => {
        const queryKey = `${selectedServiceCategoryName}:${selectedResidentId}`;
        const fetchServices = async () => {
            const isNewQuery = lastQueryKeyRef.current !== queryKey;
            if (isNewQuery) {
                lastQueryKeyRef.current = queryKey;
                setSelectedServiceId('');
                if (page_no !== 1) {
                    setPage_no(1);
                    setServices([]);
                    setHasMore(false);
                    setServiceImages({});
                    return;
                }
                setServices([]);
                setHasMore(false);
                setServiceImages({});
            }
            const facilityId = FacilityId;
            if (!facilityId) {
                return;
            }
            setIsFetching(true);
            try {
                const services = await fetchAllServicesInCategory({
                    pageNo: page_no,
                    pageSize: page_size,
                    facilityId,
                    category: selectedServiceCategoryName,
                    showActiveCategories: true,
                    bookableOnly: true,
                    residentId: selectedResidentId,
                });
                // A slower request for a category/resident the user has since
                // navigated away from must not overwrite the current results.
                if (lastQueryKeyRef.current !== queryKey) return;
                const allServices: Service[] = services?.Result?.Content?.Result || [];
                const totalRecords = services?.Result?.Content?.TotRecords || 0;
                const hasMoreServices = totalRecords > page_no * page_size;
                setHasMore(hasMoreServices);
                if (allServices.length > 0) {
                    setServices((prevServices) => {
                        const existingIds = new Set(prevServices.map((service) => service._id));
                        const newServices = allServices.filter((service) => !existingIds.has(service._id));
                        return [...prevServices, ...newServices];
                    });
                    // Best-effort: a single broken image must not hide the
                    // otherwise-successfully-fetched service list.
                    fetchServiceImages(allServices, FacilityId).then((imagesMap) => {
                        if (lastQueryKeyRef.current !== queryKey) return;
                        setServiceImages((prevImages) => ({ ...prevImages, ...imagesMap }));
                    });
                }
            } catch (error) {
                sendToast('error', error instanceof Error ? error.message : 'An error occurred while fetching services');
            } finally {
                if (lastQueryKeyRef.current === queryKey) setIsFetching(false);
            }
        };
        fetchServices();
    }, [FacilityId, selectedServiceCategoryName, selectedResidentId, page_no, page_size, setIsFetching, setSelectedServiceId]);

    const fetchServiceImages = async (services: Service[], FacilityId: string) => {
        const servicesWithImages = services.filter((service) => service.image);
        const results = await settleAll(
            servicesWithImages.map(async (service) => {
                const imageUrl = await fetchServiceImage({
                    serviceId: service._id,
                    fileName: service.image,
                    Facility: FacilityId || '',
                });
                return { id: service._id, imageUrl };
            }),
        );
        const imagesMap: Record<string, string> = {};
        results.forEach((result) => {
            if (result.status === 'fulfilled') {
                imagesMap[result.value.id] = result.value.imageUrl;
            }
        });
        return imagesMap;
    };

    return (
        <div>
            {services.length > 0 ? (
                <div className="service-list">
                    {services.map((service) => (
                        <ServiceListCard
                            key={service._id}
                            service={service}
                            serviceImage={serviceImages[service._id] || ''}
                            FacilityId={FacilityId}
                            isSelected={selectedServiceId === service._id}
                            onSelect={() => setSelectedServiceId(service._id)}
                        />
                    ))}
                </div>
            ) : (
                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    No services available for this category.
                </div>
            )}
            <div style={{ marginTop: '10px' }}>
                <LoadMore
                    isLoading={isFetching}
                    hasMore={hasMore}
                    next={() => setPage_no((prev) => prev + 1)}
                    skipMargin={true}
                />
            </div>
        </div>
    );
};

export default CreateOrderStepTwo;
